import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "app/hooks";

// Components
import { useSnackBar } from "components/SnackBar";

// Others
import { urlPaths } from "enums/urlPaths";
import {
    SnackBarEntityTypes,
    TConvertedPeopleList,
    TConvertedCompaniesList,
    convertProjectPerson,
    convertProjectCompany,
    getBriefList,
} from "pages/ProjectDashboard/adapters/formattedData";
import useRequestSet from "utils/hooks/useRequestSet";

// Store
import { clearChart } from "store/slice/charts";

// Styles

const selectedEntityInitialState: {
    type: keyof typeof SnackBarEntityTypes | undefined;
    data: (TConvertedPeopleList | TConvertedCompaniesList)[];
} = {
    type: undefined,
    data: [],
};

const useBriefCard = ({
    data,
    onClickCreateSearchAi,
}: {
    data: ReturnType<typeof getBriefList>[0];
    onClickCreateSearchAi?: () => void;
}) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { shouldOpenSnackBar, popOverAnchorEl, handleOpenSnackBar, handleCloseSnackBar } = useSnackBar();
    const { requestSets } = useRequestSet();

    const wrapperRef = useRef<HTMLElement | null | undefined>(null);
    const [selectedEntity, setSelectedEntity] = useState<typeof selectedEntityInitialState>(selectedEntityInitialState);

    const [hoverChipId] = useState<number | null>(null);
    const [clickedChipId, setClickedChipId] = useState<number | null>(null);

    const chips = [
        {
            id: 1,
            label: "Company",
            count: data.meta.companies_count,
        },
        {
            id: 2,
            label: "People",
            count: data.meta.people_count,
        },
        {
            id: 3,
            label: "Shortlist",
            count: data.meta.shortlist_count,
        },
    ];

    const handleClickCircledCount = async (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>,
        type: keyof typeof SnackBarEntityTypes,
        chip: typeof chips[0]
    ) => {
        if (chip.count === 0) return;

        setSelectedEntity((prevVal) => ({ ...prevVal, type }));
        handleOpenSnackBar(event);
        setClickedChipId(chip.id);

        if (type === "company") {
            const response = await requestSets(data.meta.projectId, data.relatedLists.company as string[], type);
            const list: TConvertedCompaniesList[] = response.map((p: any) => convertProjectCompany(p.data));
            setSelectedEntity((prevVal) => ({ ...prevVal, data: list }));
        }

        if (type === "people") {
            const response = await requestSets(data.meta.projectId, data.relatedLists.people as string[], type);
            const list: TConvertedPeopleList[] = response.map((p: any) => convertProjectPerson(p.data));
            setSelectedEntity((prevVal) => ({ ...prevVal, data: list }));
        }

        if (type === "shortlist") {
            const response = await requestSets(data.meta.projectId, data.relatedLists.shortList as string[], "people");
            const list: TConvertedPeopleList[] = response.map((p: any) => convertProjectPerson(p.data));
            setSelectedEntity((prevVal) => ({ ...prevVal, data: list }));
        }
    };

    const handleGotoBrief = (path: string) => {
        dispatch(clearChart());
        navigate(
            `${path}?projectId=${data.meta.projectId}&insightId=${data.meta.relatedInsight?.id}&briefId=${data.id}`,
            {
                state: {
                    from: `${urlPaths.ProjectDashboard}/${data.meta.projectId}`,
                },
            }
        );
    };

    const handleCreateSearchAI = () => {
        onClickCreateSearchAi && onClickCreateSearchAi();
    };

    return {
        chips,
        wrapperRef,
        shouldOpenSnackBar,
        popOverAnchorEl,
        selectedEntity,
        hoverChipId,
        clickedChipId,
        setSelectedEntity,
        setClickedChipId,
        handleClickCircledCount,
        handleGotoBrief,
        handleCreateSearchAI,
        handleCloseSnackBar,
    };
};

export default useBriefCard;
